import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Season } from '../../../../../corelogic/domain/models/season.model';
import { getSeasonId } from '../../../../../corelogic/state/seasonFights/seasonFights.selector';
import { store } from '../../../../../corelogic/state/store';
import { useSeasonState } from '../../hooks/useSeasonState';

interface SeasonSelectorProps {
  selectedSeasonId: string | null;
  onSeasonChange: (seasonId: string) => void;
  variant?: 'default' | 'compact';
}

const SeasonSelector: React.FC<SeasonSelectorProps> = ({
  selectedSeasonId,
  onSeasonChange,
  variant = 'default',
}) => {
  const { seasons } = useSeasonState();
  const [showOldSeasons, setShowOldSeasons] = useState(false);
  const [showActiveSeasons, setShowActiveSeasons] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const today = new Date();

  // Split seasons into current/future and past
  const { currentAndFutureSeasons, pastSeasons } = seasons.reduce<{
    currentAndFutureSeasons: Season[];
    pastSeasons: Season[];
  }>(
    (acc, season) => {
      const endDate = new Date(season.endDate);
      if (endDate >= today) {
        acc.currentAndFutureSeasons.push(season);
      } else {
        acc.pastSeasons.push(season);
      }
      return acc;
    },
    {
      currentAndFutureSeasons: [],
      pastSeasons: [],
    },
  );

  // Sort current/future seasons by start date
  const sortedCurrentSeasons = currentAndFutureSeasons.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );

  //Sort past seasons by start date
  const sortedPastSeasons = pastSeasons.sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
  );

  // Find current season for default selection when no season is selected
  useEffect(() => {
    if (
      !selectedSeasonId &&
      !getSeasonId(store.getState()) &&
      sortedCurrentSeasons.length > 0
    ) {
      const currentSeason = sortedCurrentSeasons[0];
      onSeasonChange(currentSeason.seasonId);
    }
  }, [selectedSeasonId, sortedCurrentSeasons, onSeasonChange]);

  const commonStyles = {
    fontFamily: 'Bebas Neue',
    fontSize: 'clamp(0.75rem, 1.8vw, 1rem)',
    textAlign: 'left' as const,
    justifyContent: 'flex-start',
    height: '30px',
    minHeight: '30px',
    padding: '6px 10px',
  };

  const buttonStyles = {
    ...commonStyles,
    backgroundColor: 'rgba(200, 200, 200, 0.5)',
    minWidth: variant === 'compact' ? '120px' : 'fit-content',
    width: variant === 'compact' ? '120px' : '100%',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '4px',
    lineHeight: 1.1,
    '&:hover': {
      backgroundColor: 'rgba(200, 200, 200, 0.7)',
    },
  };

  const menuPaperStyles = {
    backgroundColor: 'rgba(50, 50, 50, 0.95)',
    '& .MuiList-root': {
      padding: 0,
    },
    '& .MuiMenuItem-root': {
      ...commonStyles,
    },
  };

  const headerStyles = {
    ...commonStyles,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '3px',
    color: 'white',
    gap: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  };

  const menuItemStyles = {
    ...commonStyles,
    color: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
  };

  const expandIconStyles = (isExpanded: boolean) => ({
    transform: isExpanded ? 'rotate(180deg)' : 'none',
    transition: '0.3s',
  });

  const iconStyle = {
    fontSize: 'clamp(0.75rem, 1.8vw, 1rem)',
    color: 'white',
    opacity: 0.8,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: variant === 'compact' ? 'auto' : '100%',
        '& .MuiMenu-paper': {
          left: {
            xs:
              variant === 'compact'
                ? 'calc(50% + 8px) !important'
                : '50% !important',
            sm: 'unset !important',
          },
          transform: {
            xs:
              variant === 'compact'
                ? 'translateX(-50%) !important'
                : 'translateX(-50%) !important',
            sm: 'none !important',
          },
          width: {
            xs: variant === 'compact' ? 'auto' : 'calc(100vw - 32px)',
            sm: 'auto',
          },
          maxWidth: '100%',
        },
      }}
    >
      <Button onClick={(e) => setAnchorEl(e.currentTarget)} sx={buttonStyles}>
        {selectedSeasonId
          ? seasons.find((s) => s.seasonId === selectedSeasonId)?.seasonName
          : 'Select Season'}
        <ExpandMoreIcon sx={iconStyle} />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            ...menuPaperStyles,
            minWidth: anchorEl?.offsetWidth,
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        transitionDuration={0}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {/* Active Seasons Section */}
        <MenuItem
          onClick={() => setShowActiveSeasons(!showActiveSeasons)}
          sx={headerStyles}
        >
          Active Seasons
          <ExpandMoreIcon
            sx={{
              ...iconStyle,
              ...expandIconStyles(showActiveSeasons),
            }}
          />
        </MenuItem>

        <Collapse in={showActiveSeasons}>
          {sortedCurrentSeasons.map((season) => (
            <MenuItem
              key={season.seasonId}
              value={season.seasonId}
              selected={season.seasonId === selectedSeasonId}
              onClick={() => {
                onSeasonChange(season.seasonId);
                setAnchorEl(null);
              }}
              sx={menuItemStyles}
            >
              {season.seasonName}
            </MenuItem>
          ))}
        </Collapse>

        {/* Past Seasons Section - Remove Fragment */}
        {pastSeasons.length > 0 && [
          <MenuItem
            key="past-seasons-header"
            onClick={() => setShowOldSeasons(!showOldSeasons)}
            sx={headerStyles}
          >
            Past Seasons
            <ExpandMoreIcon
              sx={{
                ...iconStyle,
                ...expandIconStyles(showOldSeasons),
              }}
            />
          </MenuItem>,
          <Collapse key="past-seasons-collapse" in={showOldSeasons}>
            {sortedPastSeasons.map((season) => (
              <MenuItem
                key={season.seasonId}
                value={season.seasonId}
                selected={season.seasonId === selectedSeasonId}
                onClick={() => {
                  onSeasonChange(season.seasonId);
                  setAnchorEl(null);
                }}
                sx={menuItemStyles}
              >
                {season.seasonName}
              </MenuItem>
            ))}
          </Collapse>,
        ]}
      </Menu>
    </Box>
  );
};

export default SeasonSelector;
